import { __assign, __read, __rest } from "tslib";
import { useCallback, useEffect, useMemo, useRef, } from 'react';
import { combineProps, useForceUpdate, useControlled, useId, useKeyboardMoveFocus, } from '../../../../foundation';
import { DEFAULT_GET_OPTION_LABEL, isItemCanSelected, } from '../../utils/DownshiftUtils';
import { useDownshiftGroup } from '../../utils/useDownshiftGroup';
var DEFAULT_HIGHLIGHTED_INDEX = -1;
var componentName = 'useSuggestionList';
/**
 * provide suggestion list with search function and expandable group with virtualized list
 */
export var useSuggestionList = function (_a) {
    var id = _a.id, inputValueProp = _a.inputValue, _b = _a.getOptionLabel, getOptionLabel = _b === void 0 ? DEFAULT_GET_OPTION_LABEL : _b, filterOptions = _a.filterOptions, disabledItemsHighlightable = _a.disabledItemsHighlightable, options = _a.options, onInputChangeProp = _a.onInputChange, inputRef = _a.inputRef, onKeyDownProp = _a.onKeyDown, onSelect = _a.onSelect, onClear = _a.onClear, getOptionDisabled = _a.getOptionDisabled, groupBy = _a.groupBy, onGroupExpanded = _a.onGroupExpanded, groupVariant = _a.groupVariant, groupExpanded = _a.groupExpanded, groupDefaultExpanded = _a.groupDefaultExpanded, getExpandIconProps = _a.getExpandIconProps, processFilteredResult = _a.processFilteredResult;
    var isTitleMode = groupVariant === 'normal';
    var _c = __read(useControlled({
        controlled: inputValueProp,
        default: '',
        name: componentName,
    }), 2), inputValue = _c[0], setInputValue = _c[1];
    var updateInputValue = function (newValue) {
        onInputChangeProp === null || onInputChangeProp === void 0 ? void 0 : onInputChangeProp(newValue);
        setInputValue(newValue);
    };
    var focusInput = function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    var currUniqueId = useId(id || 'suggestion-list', !id);
    var highlightedIndexRef = useRef(DEFAULT_HIGHLIGHTED_INDEX);
    var changeHighlightedIndexReasonRef = useRef();
    var forceUpdate = useForceUpdate();
    var getFilteredItems = useCallback(function (items) {
        if (filterOptions) {
            return filterOptions(items, {
                inputValue: inputValue,
                getOptionLabel: getOptionLabel,
                selectedItems: [],
            });
        }
        return items;
    }, [filterOptions, getOptionLabel, inputValue]);
    var filteredResult = useMemo(function () {
        // * only when isOpen calculate the filtered result
        var results = getFilteredItems(options);
        results = processFilteredResult
            ? processFilteredResult(results, inputValue)
            : results;
        return results;
    }, [getFilteredItems, inputValue, options, processFilteredResult]);
    var _d = useDownshiftGroup({
        id: currUniqueId,
        options: options,
        filteredResult: filteredResult,
        groupExpanded: groupExpanded,
        groupDefaultExpanded: groupDefaultExpanded,
        groupVariant: groupVariant,
        groupBy: groupBy,
        getExpandIconProps: getExpandIconProps,
        onGroupExpanded: onGroupExpanded,
        getOptionDisabled: getOptionDisabled,
    }), groupedResult = _d.groupedResult, handleGroupExpandedChange = _d.handleGroupExpandedChange, optionsGroupList = _d.optionsGroupList;
    var optionItems = groupBy ? groupedResult : filteredResult;
    var setHighlightedIndex = function (index, _a) {
        var _b = _a.reRender, reRender = _b === void 0 ? false : _b, reason = _a.reason;
        changeHighlightedIndexReasonRef.current = reason;
        if (highlightedIndexRef.current !== index) {
            highlightedIndexRef.current = index;
            if (reRender)
                forceUpdate();
        }
    };
    var handleInputChange = function (newValue) {
        if (inputValue !== newValue) {
            setHighlightedIndex(DEFAULT_HIGHLIGHTED_INDEX, { reason: 'auto' });
            updateInputValue(newValue);
        }
        return false;
    };
    var getIsItemCanSelected = function (item) {
        return ((!!item && item.freeSolo) ||
            (isItemCanSelected(item) && !(getOptionDisabled === null || getOptionDisabled === void 0 ? void 0 : getOptionDisabled(item))));
    };
    var selectItem = function (e, selectedItem) {
        if (getIsItemCanSelected(selectedItem)) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(e, selectedItem);
            return true;
        }
        return false;
    };
    var clearInput = function () {
        if (inputRef.current && inputRef.current.value.length > 0) {
            updateInputValue('');
        }
    };
    var reset = function (isFocus) {
        clearInput();
        onInputChangeProp === null || onInputChangeProp === void 0 ? void 0 : onInputChangeProp('');
        if (isFocus)
            focusInput();
    };
    var _e = useKeyboardMoveFocus({
        options: optionItems,
        focusedIndexRef: highlightedIndexRef,
        infinite: true,
        onFocusedIndexChange: function (event, toIndex) {
            setHighlightedIndex(toIndex, { reason: 'keyboard', reRender: true });
            event === null || event === void 0 ? void 0 : event.preventDefault();
        },
        getOptionDisabled: disabledItemsHighlightable
            ? undefined
            : function (child) {
                return !getIsItemCanSelected(child);
            },
    }), onKeyFocusedIndexHandle = _e.onKeyFocusedIndexHandle, getNextFocusableOption = _e.getNextFocusableOption;
    var getItemProps = function (_a) {
        var item = _a.item, _b = _a.index, index = _b === void 0 ? 0 : _b, itemRest = __rest(_a, ["item", "index"]);
        var currGroup = item.group;
        var isGroupTitle = item === (currGroup === null || currGroup === void 0 ? void 0 : currGroup.options[0]);
        var itemCount = optionItems.length;
        var groupOrder = (currGroup === null || currGroup === void 0 ? void 0 : currGroup.order) || 0;
        return combineProps(__assign({ id: currUniqueId + "-option-" + index, onClick: function (e) {
                selectItem(e, item);
            }, onMouseOver: function () {
                if (highlightedIndexRef.current !== index &&
                    getIsItemCanSelected(item)) {
                    setHighlightedIndex(index, { reason: 'mouse', reRender: true });
                }
            } }, (isTitleMode && isGroupTitle
            ? {}
            : {
                role: 'option',
                'aria-setsize': itemCount - (isTitleMode ? (optionsGroupList === null || optionsGroupList === void 0 ? void 0 : optionsGroupList.length) || 0 : 0),
                'aria-posinset': index - (isTitleMode ? groupOrder + 1 : 0),
            })), itemRest);
    };
    var handleF10KeyDown = function (e) {
        var highlightedIndex = highlightedIndexRef.current;
        if (e.shiftKey) {
            var currOption = optionItems[highlightedIndex];
            var currentGroup = currOption.group;
            if (currentGroup && currentGroup.options.length > 1) {
                handleGroupExpandedChange(currentGroup.group);
            }
        }
    };
    var getInputProps = function (props) {
        return combineProps({
            id: currUniqueId + "-input",
            autoComplete: 'off',
            onChange: function (e) {
                var changeValue = e.target.value;
                handleInputChange(changeValue);
            },
            onKeyDown: function (e) {
                var _a;
                onKeyDownProp === null || onKeyDownProp === void 0 ? void 0 : onKeyDownProp(e, highlightedIndexRef.current);
                switch (e.key) {
                    case 'F10':
                        handleF10KeyDown(e);
                        break;
                    case 'Enter': {
                        var highlightedIndex = highlightedIndexRef.current;
                        if (e.which === 229)
                            return;
                        if (highlightedIndex !== DEFAULT_HIGHLIGHTED_INDEX) {
                            var currOption = optionItems[highlightedIndex];
                            var isGroupTitle = currOption === ((_a = currOption.group) === null || _a === void 0 ? void 0 : _a.options[0]);
                            if (isGroupTitle) {
                                var onClick = currOption.group.getExpandIconProps().onClick;
                                onClick === null || onClick === void 0 ? void 0 : onClick(e);
                            }
                            else {
                                selectItem(e, currOption);
                            }
                            e.stopPropagation();
                        }
                        // always preventDefault for not inset all enter into textarea
                        e.preventDefault();
                        break;
                    }
                    default:
                        onKeyFocusedIndexHandle(e);
                        break;
                }
            },
        }, props);
    };
    var getInputAriaProps = function (props) {
        return combineProps({
            // * provide for when container click focus on input
            onContainerClick: focusInput,
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': true,
            'aria-haspopup': true,
            'aria-owns': currUniqueId + "-menu",
            'aria-activedescendant': highlightedIndexRef.current > -1
                ? currUniqueId + "-option-" + highlightedIndexRef.current
                : undefined,
        }, props);
    };
    var getLabelProps = function (props) {
        return combineProps({
            htmlFor: currUniqueId + "-input",
            id: currUniqueId + "-label",
        }, props);
    };
    var getMenuProps = function (restMenuProps) {
        return combineProps({
            'aria-labelledby': currUniqueId + "-label",
            id: currUniqueId + "-menu",
            role: 'listbox',
        }, restMenuProps);
    };
    var getClearButtonProps = function (props) {
        return combineProps({
            id: currUniqueId + "-clear-button",
            onClick: function (e) {
                onClear === null || onClear === void 0 ? void 0 : onClear(e);
                reset(true);
            },
        }, props);
    };
    useEffect(function () {
        changeHighlightedIndexReasonRef.current = undefined;
    });
    return {
        reset: reset,
        clearInput: clearInput,
        forceUpdate: forceUpdate,
        focusInput: focusInput,
        updateInputValue: updateInputValue,
        selectItem: selectItem,
        onKeyFocusedIndexHandle: onKeyFocusedIndexHandle,
        getClearButtonProps: getClearButtonProps,
        getLabelProps: getLabelProps,
        getMenuProps: getMenuProps,
        getInputProps: getInputProps,
        getInputAriaProps: getInputAriaProps,
        getItemProps: getItemProps,
        highlightedIndexRef: highlightedIndexRef,
        highlightedIndex: highlightedIndexRef.current,
        optionItems: optionItems,
        inputValue: inputValue,
        onInputChange: handleInputChange,
        setHighlightedIndex: setHighlightedIndex,
        changeHighlightedIndexReasonRef: changeHighlightedIndexReasonRef,
        changeHighlightedIndexReason: changeHighlightedIndexReasonRef.current,
        optionsGroupList: optionsGroupList,
        /** current unique id */
        id: currUniqueId,
        getNextFocusableOption: getNextFocusableOption,
        handleF10KeyDown: handleF10KeyDown,
    };
};
